import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";

import {
  requestProspectusUpload,
  updateTournament,
  uploadToPresignedUrl,
  type Tournament,
} from "@/libs/tournamentApi";

const formatUploadedAt = (iso: string | null | undefined) => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export function ProspectusCard({
  tournament,
  onUpdated,
}: {
  tournament: Tournament;
  onUpdated: () => void | Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mountedRef = useRef(true);
  const sessionGenerationRef = useRef(0);
  const inFlightSessionRef = useRef<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      sessionGenerationRef.current += 1;
      inFlightSessionRef.current = null;
    };
  }, []);

  useEffect(() => {
    sessionGenerationRef.current += 1;
    inFlightSessionRef.current = null;
    setFile(null);
    setUploading(false);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }, [tournament.tournament_id]);

  const handleUpload = async () => {
    if (!file) {
      setError("Choose a PDF first.");
      return;
    }
    if (inFlightSessionRef.current !== null) return;

    const sessionGeneration = sessionGenerationRef.current;
    const tournamentId = tournament.tournament_id;
    const selectedFile = file;
    inFlightSessionRef.current = sessionGeneration;
    setError(null);
    setUploading(true);
    try {
      const upload = await requestProspectusUpload(tournamentId);
      await uploadToPresignedUrl(upload, selectedFile);
      await updateTournament(tournamentId, {
        prospectus_pdf_url: upload.cdn_url,
        prospectus_filename: selectedFile.name,
        prospectus_uploaded_at: new Date().toISOString(),
      });
      if (
        !mountedRef.current ||
        sessionGenerationRef.current !== sessionGeneration
      ) {
        return;
      }
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await onUpdated();
    } catch (cause) {
      if (
        mountedRef.current &&
        sessionGenerationRef.current === sessionGeneration
      ) {
        setError(
          cause instanceof Error ? cause.message : "Prospectus upload failed",
        );
      }
    } finally {
      if (inFlightSessionRef.current === sessionGeneration) {
        inFlightSessionRef.current = null;
      }
      if (
        mountedRef.current &&
        sessionGenerationRef.current === sessionGeneration
      ) {
        setUploading(false);
      }
    }
  };

  return (
    <Paper
      aria-labelledby="tournament-prospectus-title"
      component="section"
      variant="outlined"
    >
      <Stack spacing={2.5} sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box>
          <Typography
            component="h2"
            id="tournament-prospectus-title"
            variant="h6"
          >
            Prospectus
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Upload the PDF organizers and participants use for event details.
          </Typography>
        </Box>

        {tournament.prospectus_pdf_url ? (
          <Stack spacing={0.5}>
            <Link
              href={tournament.prospectus_pdf_url}
              rel="noreferrer"
              target="_blank"
              underline="hover"
              sx={{ alignItems: "center", display: "inline-flex", gap: 0.75 }}
            >
              View prospectus
              <OpenInNewRoundedIcon fontSize="small" />
            </Link>
            <Typography color="text.secondary" variant="body2">
              {tournament.prospectus_filename ?? "prospectus.pdf"}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              Uploaded {formatUploadedAt(tournament.prospectus_uploaded_at)}
            </Typography>
          </Stack>
        ) : (
          <Typography color="text.secondary" variant="body2">
            No prospectus uploaded.
          </Typography>
        )}

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ alignItems: { sm: "flex-end" } }}
        >
          <TextField
            disabled={uploading}
            fullWidth
            id="prospectus-file"
            inputRef={inputRef}
            label="PDF file"
            onChange={(event) =>
              setFile((event.target as HTMLInputElement).files?.[0] ?? null)
            }
            size="small"
            slotProps={{
              htmlInput: { accept: ".pdf,application/pdf" },
              inputLabel: { shrink: true },
            }}
            type="file"
          />
          <Button
            disabled={uploading || !file}
            onClick={handleUpload}
            startIcon={<UploadFileRoundedIcon />}
            sx={{ flexShrink: 0 }}
            variant="contained"
          >
            {uploading ? "Uploading…" : "Upload prospectus"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
